import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  applyPwaThemeToDocument,
  readStoredPwaTheme,
  writeStoredPwaTheme,
  type PwaTheme,
} from '@/lib/pwaTheme';

type PwaThemeContextValue = {
  theme: PwaTheme;
  setTheme: (theme: PwaTheme) => void;
  toggleTheme: () => void;
};

const PwaThemeContext = createContext<PwaThemeContextValue | null>(null);

export function PwaThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<PwaTheme>(() => readStoredPwaTheme());

  useEffect(() => {
    applyPwaThemeToDocument(theme);
  }, [theme]);

  const setTheme = useCallback((next: PwaTheme) => {
    setThemeState(next);
    writeStoredPwaTheme(next);
    applyPwaThemeToDocument(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return <PwaThemeContext.Provider value={value}>{children}</PwaThemeContext.Provider>;
}

export function usePwaTheme(): PwaThemeContextValue {
  const ctx = useContext(PwaThemeContext);
  if (!ctx) throw new Error('usePwaTheme fuera de PwaThemeProvider');
  return ctx;
}

/** @deprecated Usar usePwaTheme */
export const useChatTheme = usePwaTheme;
